import React, { useCallback, useMemo, useState } from "react";
import { roundToCents } from "@common/helpers/monetary";
import { Account } from "@common/types/domains/accounting/account";
import { AccrualMatchDisplay } from "@common/schemas/domains/accrualReview/accrual-review.schema";
import { MatchableItem, MatchableTransaction, MatchItemType } from "./types";
import { transactionToMatchableItem } from "./mappers";
import { AllocationDrawerBody } from "./editAllocation/AllocationDrawerLayout";
import { AllocationTable } from "./editAllocation/AllocationTable";
import {
    clampPaymentAmount,
    getIsAllocationSaveEnabled,
} from "./editAllocation/allocationHelpers";
import { buildAllocationBody } from "./editAllocation/buildAllocationBody";
import {
    buildMultiAllocationTotals,
    buildSelectedRows,
    buildSequentialPayments,
    collectPersistedLinePayments,
    flattenPayments,
    hasOverAllocatedLines,
    parsePaymentRowKey,
    PaymentsByTransaction,
    restoreDocumentOpenAmounts,
    shrinkDocumentOpenAmounts,
    sumPaymentsExcluding,
} from "./editAllocation/multiAllocationHelpers";
import { planAllocationSaves } from "./editAllocation/allocationSavePlan";
import { RemainingAllocationSection } from "./editAllocation/RemainingAllocationSection";
import { UnbalancedTransactionsSummary } from "./editAllocation/UnbalancedTransactionsSummary";
import { MultiAllocationLedgerContext } from "./editAllocation/useMultiAllocationLedgerContext";
import { AllocationViewState } from "./editAllocation/useAllocationView";
import { useMultiRemainingLines } from "./editAllocation/useMultiRemainingLines";
import { useSequentialAllocationSave } from "./editAllocation/useSequentialAllocationSave";
import styles from "./EditAllocationDrawer.module.scss";

type TransactionWithMatches = MatchableTransaction & {
    accrualMatches?: AccrualMatchDisplay[];
};

type SequentialAllocationSave = ReturnType<typeof useSequentialAllocationSave>;

interface Props {
    documentItem: MatchableItem;
    transactions: TransactionWithMatches[];
    ledgerContext: MultiAllocationLedgerContext;
    viewState: AllocationViewState;
    matchItemType: MatchItemType | null;
    save: SequentialAllocationSave["save"];
    isSaving: boolean;
    onClose: () => void;
}

function getTransactionLabel(transaction: TransactionWithMatches): string {
    return transaction.counterparty?.name ?? transaction.bankDescription;
}

export const MultiTransactionAllocationContent: React.FC<Props> = ({
    documentItem,
    transactions,
    ledgerContext,
    viewState,
    matchItemType,
    save,
    isSaving,
    onClose,
}) => {
    const { contextsByTransactionId, hasAnyAccrualLedger, showAccountNumbers } =
        ledgerContext;
    const hasAccrualLedgerFor = (transactionId: number) =>
        contextsByTransactionId.get(transactionId)?.hasAccrualLedger ?? false;
    const accountsByEntityId = useMemo(
        () =>
            Object.fromEntries(
                [...contextsByTransactionId.values()].flatMap(
                    (context): Array<[number, Account[]]> =>
                        context.entityId === undefined
                            ? []
                            : [[context.entityId, context.accounts]],
                ),
            ),
        [contextsByTransactionId],
    );

    const persistedPaymentsByTransaction = useMemo<PaymentsByTransaction>(
        () =>
            Object.fromEntries(
                transactions.map((transaction) => [
                    transaction.id,
                    collectPersistedLinePayments({
                        document: documentItem,
                        accrualMatches: transaction.accrualMatches ?? [],
                    }),
                ]),
            ),
        [transactions, documentItem],
    );
    const baseDocument = useMemo(
        () =>
            restoreDocumentOpenAmounts(
                documentItem,
                persistedPaymentsByTransaction,
            ),
        [documentItem, persistedPaymentsByTransaction],
    );

    const [payments, setPayments] = useState<PaymentsByTransaction>(() =>
        buildSequentialPayments({
            document: baseDocument,
            transactions,
            persistedPaymentsByTransaction,
        }),
    );
    const remaining = useMultiRemainingLines({
        transactions,
        contextsByTransactionId,
    });
    const handlePaymentChange = useCallback(
        (rowKey: string, value: number, maxOpen: number) => {
            const key = parsePaymentRowKey(rowKey);
            if (!key) {
                return;
            }
            setPayments((prev) => ({
                ...prev,
                [key.transactionId]: {
                    ...prev[key.transactionId],
                    [key.lineItemId]: clampPaymentAmount(value, maxOpen),
                },
            }));
        },
        [],
    );

    const transactionItems = useMemo(
        () =>
            transactions.map((transaction) => ({
                id: transaction.id,
                item: transactionToMatchableItem({
                    transaction,
                    entityIdForGLAccount: contextsByTransactionId.get(
                        transaction.id,
                    )?.entityId,
                }),
            })),
        [transactions, contextsByTransactionId],
    );
    const selectedRows = useMemo(
        () =>
            buildSelectedRows({
                document: baseDocument,
                transactionItems,
                paymentsByTransaction: payments,
            }),
        [baseDocument, transactionItems, payments],
    );
    const paymentAmounts = useMemo(() => flattenPayments(payments), [payments]);
    const sourceData = useMemo(
        () => [
            shrinkDocumentOpenAmounts(
                baseDocument,
                sumPaymentsExcluding(payments, null),
            ),
        ],
        [baseDocument, payments],
    );

    const { rows, totals } = buildMultiAllocationTotals({
        transactions,
        paymentsByTransaction: payments,
        remainingTotalsByTransaction: remaining.totalsByTransaction,
    });

    const unbalancedRows = rows.filter(
        (row) =>
            !getIsAllocationSaveEnabled({
                hasAccrualLedger: hasAccrualLedgerFor(row.transactionId),
                allocatedTotal: row.allocated,
                amountToBeAllocated: row.amount,
            }),
    );
    const labelsById = new Map(
        transactions.map((transaction) => [
            transaction.id,
            getTransactionLabel(transaction),
        ]),
    );
    const isSaveEnabled =
        !isSaving &&
        !hasOverAllocatedLines(baseDocument, payments) &&
        unbalancedRows.length === 0;

    const resolvableRows = rows.filter((row) =>
        hasAccrualLedgerFor(row.transactionId),
    );
    const hasResolvableDifference = resolvableRows.some(
        (row) => roundToCents(row.amount - row.allocated) > 0,
    );

    const handleShowRemaining = () =>
        remaining.show(
            resolvableRows.map((row) => ({
                transactionId: row.transactionId,
                amount: roundToCents(row.amount - row.linesTotal),
            })),
        );

    const handleSave = () => {
        const entries = transactions.map((transaction) => {
            const context = contextsByTransactionId.get(transaction.id);
            const selectedItems = [
                shrinkDocumentOpenAmounts(
                    baseDocument,
                    sumPaymentsExcluding(payments, transaction.id),
                ),
            ];
            const body = buildAllocationBody({
                sourceTransaction: transaction,
                selectedItems,
                paymentAmounts: payments[transaction.id] ?? {},
                remainingLines: remaining.lines.filter(
                    (line) => line.transactionId === transaction.id,
                ),
                primaryAccrualLedgerId: context?.primaryAccrualLedgerId,
                primaryCashLedgerId: context?.primaryCashLedgerId,
            });
            return {
                transactionId: transaction.id,
                body,
                hasRemainingAllocations: body.remaining !== undefined,
                selectedItems,
            };
        });
        void save(
            planAllocationSaves({
                document: baseDocument,
                entries,
                paymentsByTransaction: payments,
                persistedPaymentsByTransaction,
            }),
        );
    };

    return (
        <AllocationDrawerBody
            hasAccrualLedger={hasAnyAccrualLedger}
            totals={totals}
            summaryAddon={
                <UnbalancedTransactionsSummary
                    rows={unbalancedRows}
                    getLabel={(transactionId) =>
                        labelsById.get(transactionId) ?? ""
                    }
                />
            }
            canResolveDifference={
                !remaining.isVisible && hasResolvableDifference
            }
            onResolveDifference={handleShowRemaining}
            secondaryAction={{
                label: "Cancel",
                onClick: onClose,
                isDisabled: isSaving,
            }}
            isSaveEnabled={isSaveEnabled}
            onSave={handleSave}
        >
            <div>
                <AllocationTable
                    data={sourceData}
                    section="source"
                    matchItemType={matchItemType}
                    view={viewState.allocationView}
                    onViewChange={viewState.setAllocationView}
                />
            </div>
            <div>
                <div className={styles.sectionHeader}>Selected matches</div>
                <AllocationTable
                    data={selectedRows}
                    section="selected"
                    matchItemType={matchItemType}
                    view={viewState.allocationView}
                    onViewChange={viewState.setAllocationView}
                    paymentAmounts={paymentAmounts}
                    onPaymentChange={handlePaymentChange}
                />
            </div>
            {hasAnyAccrualLedger && remaining.isVisible && (
                <RemainingAllocationSection
                    items={remaining.items}
                    lineHandlers={remaining}
                    addLineActions={transactions
                        .filter((transaction) =>
                            hasAccrualLedgerFor(transaction.id),
                        )
                        .map((transaction) => ({
                            key: String(transaction.id),
                            label: `Add line for ${getTransactionLabel(transaction)}`,
                            onClick: () => remaining.addLine(transaction.id),
                        }))}
                    matchItemType={matchItemType}
                    view={viewState.allocationView}
                    onViewChange={viewState.setAllocationView}
                    accountsByEntityId={accountsByEntityId}
                    showAccountNumbers={showAccountNumbers}
                    amountToBeAllocated={Math.max(
                        0,
                        ...resolvableRows.map((row) => row.amount),
                    )}
                />
            )}
        </AllocationDrawerBody>
    );
};
